<template>
    <div class="resource-modal-root">
        <CustomDialog v-model="internalShow" :defaultTitle="title" heightClass="ppv2-max-h-[85vh]">
            <template #content>
                <!-- Info Message -->
                <div
                    class="ppv2-flex ppv2-items-start ppv2-gap-3 ppv2-px-2 ppv2-py-3 ppv2-bg-[#2C3FE8] ppv2-bg-opacity-10 ppv2-rounded-[10px] ppv2-mb-6 ppv2-border ppv2-border-[#2C3FE8]"
                >
                    <Info :size="20" class="ppv2-text-[#2C3FE8] ppv2-flex-shrink-0 ppv2-mt-0.5" />
                    <div class="ppv2-text-sm ppv2-text-black poppinsRegular">
                        <p class="ppv2-mb-0" v-if="resourceType === 'materiel'">
                            {{
                                $t('equipements.modal.info_materiel') ||
                                'Gérez ici vos équipements et matériels mis à disposition.'
                            }}
                        </p>
                        <p class="ppv2-mb-0" v-else>
                            {{ $t('equipements.modal.info_salle') || 'Gérez ici vos installations et espaces de réservation.' }}
                        </p>
                    </div>
                </div>

                <div v-if="resourceType === 'materiel'">
                    <div class="ppv2-space-y-4">
                        <!-- Name -->
                        <CustomInput
                            v-model="formMateriel.name"
                            :state="getState('name', 'materiel')"
                            :errorText="errors.materiel?.name"
                            :label="$t('equipements.form.name.label')"
                            :placeholder="$t('equipements.form.name.placeholder')"
                            :required="true"
                            @input="validateField('name', 'materiel')"
                        />

                        <!-- Status -->
                        <BaseSelect
                            v-model="formMateriel.status"
                            :error="errors.materiel?.status"
                            :label="$t('equipements.form.type.label')"
                            :options="option_type"
                            :required="true"
                            @input="validateField('status', 'materiel')"
                            fixed-dropdown
                        />

                        <!-- Capacity -->
                        <CustomInput
                            v-model="formMateriel.capacity"
                            :state="getState('capacity', 'materiel')"
                            :errorText="errors.materiel?.capacity"
                            :label="$t('equipements.form.capacity.label')"
                            :placeholder="$t('equipements.form.capacity.placeholder')"
                            :required="true"
                            type="number"
                            @input="validateField('capacity', 'materiel')"
                        />

                        <!-- Disable (only edit) -->
                        <CustomInput
                            v-if="hasId"
                            v-model="formMateriel.diseable"
                            :state="getState('diseable', 'materiel')"
                            :errorText="errors.materiel?.diseable"
                            :label="$t('equipements.form.diseable.label')"
                            :placeholder="$t('equipements.form.diseable.placeholder')"
                            type="number"
                            @input="validateField('diseable', 'materiel')"
                        />
                    </div>
                </div>
                <div v-else>
                    <div class="ppv2-space-y-4">
                        <!-- Name -->
                        <CustomInput
                            v-model="formSalle.name"
                            :state="getState('name', 'salle')"
                            :errorText="errors.salle?.name"
                            :label="$t('spaces.form.name.label')"
                            :placeholder="$t('spaces.form.name.placeholder')"
                            :required="true"
                            @input="validateField('name', 'salle')"
                        />

                        <!-- Capacity -->
                        <CustomInput
                            v-model="formSalle.capacity"
                            type="number"
                            :state="getState('capacity', 'salle')"
                            :errorText="errors.salle?.capacity"
                            :label="$t('spaces.form.capacity.label')"
                            :placeholder="$t('spaces.form.capacity.placeholder')"
                            :required="true"
                            @input="validateField('capacity', 'salle')"
                        />

                        <!-- Mixing switch -->
                        <div class="ppv2-flex ppv2-flex-col ppv2-gap-2">
                            <BaseSwitch
                                v-model="formSalle.authorize_mixing"
                                :label="$t('spaces.form.switches.authorize_mixing')"
                            />

                            <!-- Karafun -->
                            <BaseSwitch
                                v-model="formSalle.karafun"
                                :label="$t('experience.step3.installation.karafun')"
                                :disabled="!isKarafunConfigured"
                            />
                            <div v-if="formSalle.karafun && isKarafunConfigured" class="ppv2-mt-2 ppv2-pl-4">
                                <KarafunDevice
                                    :selectedDevices.sync="formSalle.devices"
                                    :devices="karafunDevices"
                                    @sync="fetchKarafunDevices"
                                />
                            </div>
                            <InfoBanner v-if="!isKarafunConfigured" variant="warning" class="ppv2-mt-3" :customFontSize="12">
                                {{ $t('experience.step3.installation.karafunNotConfigured') }}
                                <a href="/back-office/v3/integration" target="_blank" class="poppinsSemiBold ppv2-underline">{{
                                    $t('experience.step3.installation.karafunGoToIntegration')
                                }}</a>
                            </InfoBanner>
                        </div>

                        <!-- Horaires de l'installation -->
                        <DashedCard
                            :title="$t('spaces.form.hours.customTitle')"
                            :marginTop="20"
                            :marginBottom="10"
                            :custom-style="{ padding: '24px' }"
                        >
                            <template #content>
                                <InfoBanner v-if="linkedCount > 0" variant="warning" class="ppv2-my-2 ppv2-mx-[5%]">
                                    {{ $tc('spaces.form.hours.sharedSpaceWarning', linkedCount, { count: linkedCount }) }}
                                </InfoBanner>
                                <div
                                    class="ppv2-my-2 ppv2-mx-[5%] ppv2-w-1/2 ppv2-min-h-[250px] ppv2-min-w-[200px] ppv2-border ppv2-border-black ppv2-border-opacity-50 ppv2-rounded-[12px]"
                                >
                                    <h2
                                        class="poppinsBold ppv2-my-4 ppv2-text-black ppv2-pt-6 ppv2-w-[70%] ppv2-mx-auto"
                                        :style="{ fontSize: '15px' }"
                                    >
                                        {{ $t('configuration.cards.schedule.title') }}
                                    </h2>
                                    <ul class="schedule-list ppv2-mb-2">
                                        <li
                                            v-for="(day, i) in days"
                                            :key="i"
                                            class="evenItem poppinsLight ppv2-text-black ppv2-py-2"
                                            :style="{ fontSize: '12px' }"
                                        >
                                            <div
                                                class="ppv2-flex ppv2-justify-between ppv2-items-center ppv2-w-[70%] ppv2-mx-auto"
                                            >
                                                <span>{{ $t('resource_planning.week_days.' + day.name.toLowerCase()) }} :</span>
                                                <span>
                                                    <span v-if="day.enabled">
                                                        <span
                                                            v-for="(line, si) in dayScheduleLines(day)"
                                                            :key="si"
                                                            class="ppv2-block"
                                                        >
                                                            {{ line }}
                                                        </span>
                                                    </span>
                                                    <span v-else>
                                                        {{ $t('resource_planning.exceptions.exception_types.closed') }}
                                                    </span>
                                                </span>
                                            </div>
                                        </li>
                                    </ul>
                                    <div class="ppv2-flex ppv2-justify-center ppv2-items-center">
                                        <BaseButton
                                            type="button"
                                            @click="showScheduleDialog = true"
                                            variant="secondary"
                                            class="ppv2-mt-2 ppv2-mb-6"
                                            :customFontSize="12"
                                        >
                                            {{ $t('experience.step4.multiMode.schedule.btnEdit') }}
                                        </BaseButton>
                                    </div>
                                </div>
                            </template>
                        </DashedCard>
                        <PersonalizeSchedules
                            v-model="showScheduleDialog"
                            :days="days"
                            @schedule-updated="handleScheduleUpdate"
                        />
                    </div>
                </div>
            </template>

            <template #actions>
                <BaseButton type="button" variant="outline" @click="close" customClass="!ppv2-h-[45px] !ppv2-min-w-[150px]">
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton variant="primary" @click="save" :loading="isLoading" customClass="!ppv2-h-[45px] !ppv2-min-w-[150px]">
                    {{ $t('common.save') }}
                </BaseButton>
            </template>
        </CustomDialog>
        <!-- Hors du CustomDialog (slot par défaut non rendu) : comme dans Step4 Expérience,
         la modale doit être sœur racine pour s'afficher par-dessus tout. -->
        <SharedSpaceWarningModal
            v-model="showSharedSpaceModal"
            :sharedSpaces="sharedSpacesData"
            @confirm="onSharedSpaceConfirm"
            @cancel="onSharedSpaceCancel"
        />
    </div>
</template>

<script>
import { Info } from 'lucide-vue'
import CustomDialog from '../../../Shared/CustomDialog.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import DashedCard from '../../../Shared/DashedCard.vue'
import PersonalizeSchedules from '../../Experience/components/PersonalizeSchedules.vue'
import SharedSpaceWarningModal from '../../Experience/components/SharedSpaceWarningModal.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import InfoBanner from '../../../Shared/InfoBanner.vue'
import KarafunDevice from '../../Experience/components/KarafunDevice.vue'
import axios from 'axios'

export default {
    name: 'ResourceModal',
    components: {
        Info,
        CustomDialog,
        CustomInput,
        BaseSelect,
        BaseButton,
        BaseSwitch,
        KarafunDevice,
        InfoBanner,
        DashedCard,
        PersonalizeSchedules,
        SharedSpaceWarningModal,
    },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        resourceType: {
            type: String,
            required: true, // 'materiel' or 'salle' (installation)
        },
        item: {
            type: Object,
            default: null,
        },
        token: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            isLoading: false,
            loadingHours: false,
            errors: {
                materiel: {},
                salle: {},
            },
            formMateriel: {
                id: -1,
                name: '',
                capacity: 0,
                status: 1,
                diseable: 0,
            },
            formSalle: {
                id: -1,
                name: '',
                capacity: 0,
                type: 0,
                authorize_mixing: 0,
                link_space: false,
                karafun: false,
                devices: [],
                has_custom_hours: 1,
            },
            karafunDevices: [],
            loadingKarafun: false,
            isKarafunConfigured: false,
            option_type: [
                { label: this.$t('equipements.form.options.disabled'), value: 0 },
                { label: this.$t('equipements.form.options.free'), value: 1 },
                { label: this.$t('equipements.form.options.repair'), value: 2 },
            ],
            // Horaires personnalisés (même modèle que Step4 Expérience)
            days: [],
            showScheduleDialog: false,
            showSharedSpaceModal: false,
            sharedSpacesData: [],
            linkedCount: 0,
            initialScheduleString: null,
            sharedWarningConfirmed: false,
        }
    },
    computed: {
        internalShow: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        hasId() {
            return this.currentForm.id !== -1
        },
        title() {
            if (this.resourceType === 'materiel') {
                return this.hasId ? this.$t('equipements.form.title_edit') : this.$t('equipements.form.title_create')
            }
            return this.hasId ? this.$t('spaces.form.title_edit') : this.$t('spaces.form.title_create')
        },
        currentForm() {
            return this.resourceType === 'materiel' ? this.formMateriel : this.formSalle
        },
    },
    watch: {
        async value(newVal) {
            if (newVal) {
                if (this.resourceType === 'salle') {
                    // Resolve the Karafun integration state BEFORE building the form,
                    // otherwise initForm() force-resets the karafun toggle to false.
                    await this.checkKarafunIntegration()
                    if (this.karafunDevices.length === 0) {
                        this.fetchKarafunDevices()
                    }
                }
                this.initForm()
                if (this.resourceType === 'salle') {
                    this.fetchSpaceHours()
                    this.fetchLinkedExperiences()
                }
            } else {
                this.resetForm()
            }
        },
    },
    methods: {
        normalizeTimeDisplay(v) {
            const s = String(v || '').trim()
            const m = s.match(/^(\d{1,2})\s*:\s*(\d{1,2})(?:\s*:\s*\d{1,2})?$/)
            if (m) return `${String(m[1]).padStart(2, '0')}:${String(m[2]).padStart(2, '0')}`
            if (/^\d{1,2}$/.test(s)) return `${s.padStart(2, '0')}:00`
            return s.slice(0, 5)
        },
        dayScheduleLines(day) {
            if (!day || !day.enabled || day.close) return []
            const period = day.periods && day.periods[0]
            if (!period || !period.start || !period.end) return []
            const short = (v) => this.normalizeTimeDisplay(v)
            const lunch = day.lunches && day.lunches[0]
            const hasLunch =
                lunch &&
                lunch.start &&
                lunch.end &&
                lunch.start !== lunch.end &&
                !(lunch.start === '00:00' && lunch.end === '00:00')
            if (!hasLunch) return [`${short(period.start)} - ${short(period.end)}`]
            return [`${short(period.start)} - ${short(lunch.start)}`, `${short(lunch.end)} - ${short(period.end)}`]
        },
        mapApiToDays(apiData) {
            if (!apiData || !Array.isArray(apiData)) return this.initializeEmptyDays()
            const dayMap = [
                { name: 'monday', label: this.$t('resource_planning.week_days.monday'), day: 1 },
                { name: 'tuesday', label: this.$t('resource_planning.week_days.tuesday'), day: 2 },
                { name: 'wednesday', label: this.$t('resource_planning.week_days.wednesday'), day: 3 },
                { name: 'thursday', label: this.$t('resource_planning.week_days.thursday'), day: 4 },
                { name: 'friday', label: this.$t('resource_planning.week_days.friday'), day: 5 },
                { name: 'saturday', label: this.$t('resource_planning.week_days.saturday'), day: 6 },
                { name: 'sunday', label: this.$t('resource_planning.week_days.sunday'), day: 0 },
            ]
            const daysMap = new Map()
            apiData.forEach((item) => {
                const adjustedDay = item.day === 0 ? 6 : item.day - 1
                const dayInfo = dayMap[adjustedDay]
                if (!dayInfo) return
                const hasLunch =
                    item.lunch_start && item.lunch_end && !(item.lunch_start === '00:00' && item.lunch_end === '00:00')
                daysMap.set(adjustedDay, {
                    name: dayInfo.name,
                    label: dayInfo.label,
                    day: dayInfo.day,
                    enabled: Number(item.is_open) === 1,
                    close: Number(item.is_open) === 0,
                    periods: [{ start: item.start, end: item.end }],
                    lunches: hasLunch ? [{ start: item.lunch_start, end: item.lunch_end }] : [],
                })
            })
            return dayMap.map((day, index) => {
                return (
                    daysMap.get(index) || {
                        name: day.name,
                        label: day.label,
                        day: day.day,
                        enabled: false,
                        periods: [],
                        lunches: [],
                        close: true,
                    }
                )
            })
        },
        initializeEmptyDays() {
            const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
            const dayNumbers = [1, 2, 3, 4, 5, 6, 0]
            return dayNames.map((name, index) => ({
                name,
                day: dayNumbers[index],
                label: this.$t(`resource_planning.week_days.${name}`),
                enabled: true,
                periods: [{ start: '09:00:00', end: '18:00:00' }],
                lunches: [],
                close: false,
            }))
        },
        toApiDays() {
            return (this.days || []).map((d) => {
                const isClosed = d.close || !d.enabled
                return {
                    name: d.name,
                    day: d.day,
                    enabled: !!d.enabled,
                    // Jour fermé : ni périodes ni pauses pour éviter la validation
                    // backend (00:00 → 00:00 stocké en base → "end doit être après start").
                    periods: isClosed
                        ? []
                        : (d.periods || []).map((p) => ({
                              start: (p.start || '').slice(0, 5) + ':00',
                              end: (p.end || '').slice(0, 5) + ':00',
                          })),
                    lunches: isClosed
                        ? []
                        : (d.lunches || [])
                              .filter((l) => l.start && l.end && l.start !== l.end)
                              .map((l) => ({
                                  start: (l.start || '').slice(0, 5) + ':00',
                                  end: (l.end || '').slice(0, 5) + ':00',
                              })),
                }
            })
        },
        getScheduleString() {
            return JSON.stringify({ has_custom_hours: this.formSalle.has_custom_hours, days: this.days })
        },
        scheduleHasChanged() {
            // Création : toujours envoyer le choix horaires (la salle n'a pas de grille).
            if (!this.hasId) return true
            if (!this.initialScheduleString) return true
            try {
                return this.initialScheduleString !== this.getScheduleString()
            } catch (e) {
                return true
            }
        },
        async fetchSpaceHours() {
            const spaceId = this.item?.id
            if (!spaceId || spaceId === -1) {
                // Création : horaires affichés d'emblée (mode personnalisé).
                this.formSalle.has_custom_hours = 1
                this.days = this.initializeEmptyDays()
                this.initialScheduleString = this.getScheduleString()
                this.sharedWarningConfirmed = false
                return
            }
            this.loadingHours = true
            try {
                const { data } = await axios.get(`/api/v3/equipements/spaces/${spaceId}/hours`, {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                this.formSalle.has_custom_hours = 1
                // Base d'édition : la grille salle si elle existe, sinon celle de
                // l'établissement en pré-remplissage.
                const source =
                    data.days && data.days.length
                        ? data.days
                        : data.establishment_days && data.establishment_days.length
                          ? data.establishment_days
                          : []
                this.days = this.mapApiToDays(source)
                if (!this.days || !this.days.length) this.days = this.initializeEmptyDays()
                this.initialScheduleString = this.getScheduleString()
                this.sharedWarningConfirmed = false
            } catch (e) {
                this.days = this.initializeEmptyDays()
                this.initialScheduleString = this.getScheduleString()
                this.sharedWarningConfirmed = false
            } finally {
                this.loadingHours = false
            }
        },
        async fetchLinkedExperiences() {
            const spaceId = this.item?.id
            this.linkedCount = this.item?.active_prestations_count || 0
            this.sharedSpacesData = []
            if (!spaceId || spaceId === -1) return
            try {
                const { data } = await axios.get(`/api/v3/equipements/spaces/${spaceId}/linked-experiences`, {
                    headers: { Authorization: `Bearer ${this.token}` },
                })
                this.linkedCount = data.count || 0
                this.sharedSpacesData = data.shared_spaces || []
            } catch (e) {
                // Le bandeau reste basé sur active_prestations_count en repli.
            }
        },
        async handleScheduleUpdate(newSchedule) {
            const changed = JSON.stringify(this.days) !== JSON.stringify(newSchedule)
            this.days = newSchedule.map((day) => ({ ...day, periods: [...day.periods], lunches: [...day.lunches] }))
            if (!changed) return
            this.sharedWarningConfirmed = false
            // Même vérification que l'Expérience : avertir si l'espace est partagé.
            if (this.linkedCount > 0) {
                await this.fetchLinkedExperiences()
                if (this.linkedCount > 0) this.showSharedSpaceModal = true
            }
        },
        async onSharedSpaceConfirm() {
            // Après « Continuer quand même », on enchaîne directement la soumission
            // de la popup espace (jours édités + formulaire), sans clic supplémentaire.
            this.showSharedSpaceModal = false
            this.sharedWarningConfirmed = true
            await this.proceedSave()
        },
        onSharedSpaceCancel() {
            this.showSharedSpaceModal = false
            this.sharedWarningConfirmed = false
            if (this.initialScheduleString) {
                const initial = JSON.parse(this.initialScheduleString)
                this.formSalle.has_custom_hours = 1
                this.days = initial.days
            }
        },
        initForm() {
            if (this.item && this.item.id && this.item.id !== -1) {
                if (this.resourceType === 'materiel') {
                    this.formMateriel = {
                        id: this.item.id,
                        name: this.item.name || '',
                        capacity: this.item.quantity || 0,
                        status:
                            this.item.status_value !== undefined ? this.item.status_value : this.item.status === 'actif' ? 1 : 0,
                        diseable: this.item.diseable || 0,
                    }
                } else {
                    this.formSalle = {
                        id: this.item.id,
                        name: this.item.name || '',
                        capacity: this.item.capacity || 0,
                        type: this.item.type || 0,
                        authorize_mixing: this.item.authorize_mixing || 0,
                        link_space: false,
                        karafun: this.item.karafun_devices?.length > 0,
                        devices: this.item.karafun_devices?.map((d) => d.id) || [],
                        // Sans sélecteur : les horaires sont toujours éditables (mode personnalisé).
                        has_custom_hours: 1,
                    }
                    if (!this.isKarafunConfigured) {
                        this.formSalle.karafun = false
                    }
                    // Les horaires arrivent via fetchSpaceHours (watcher).
                    this.days = this.initializeEmptyDays()
                    this.sharedSpacesData = []
                    this.linkedCount = this.item.active_prestations_count || 0
                    this.initialScheduleString = null
                    this.sharedWarningConfirmed = false
                }
            } else {
                this.resetForm()
            }
        },
        resetForm() {
            this.formMateriel = {
                id: -1,
                name: '',
                capacity: 0,
                status: 1,
                diseable: 0,
            }
            this.formSalle = {
                id: -1,
                name: '',
                capacity: 0,
                type: 0,
                authorize_mixing: 0,
                link_space: false,
                karafun: false,
                devices: [],
                has_custom_hours: 1,
            }
            this.errors = {
                materiel: {},
                salle: {},
            }
            this.days = this.initializeEmptyDays()
            this.showScheduleDialog = false
            this.showSharedSpaceModal = false
            this.sharedSpacesData = []
            this.linkedCount = 0
            this.initialScheduleString = null
            this.sharedWarningConfirmed = false
        },
        getState(field, type) {
            const err = this.errors[type][field]
            if (err) return 'error'
            if (this[type === 'materiel' ? 'formMateriel' : 'formSalle'][field]) return 'valid'
            return 'default'
        },
        validateField(field, type) {
            const form = type === 'materiel' ? this.formMateriel : this.formSalle
            const err = {}

            if (field === 'name' && !form.name) err.name = this.$t('experience.errors.required')
            if (field === 'capacity' && (form.capacity === null || form.capacity === ''))
                err.capacity = this.$t('experience.errors.required')

            if (type === 'materiel' && field === 'status' && (form.status === null || form.status === ''))
                err.status = this.$t('experience.errors.required')

            this.$set(this.errors[type], field, err[field])

            if (!err[field]) {
                delete this.errors[type][field]
            }

            return !err[field]
        },
        async fetchKarafunDevices() {
            this.loadingKarafun = true
            try {
                const response = await axios.get('/api/v1/karafun/list')
                this.karafunDevices = response.data.devices || response.data
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.loadingKarafun = false
            }
        },
        async checkKarafunIntegration() {
            try {
                const response = await axios.get('/api/v1/karafun/check')
                this.isKarafunConfigured = response.data.configured === true
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.isKarafunConfigured = false
            }
        },
        close() {
            this.internalShow = false
        },
        async save() {
            const type = this.resourceType
            const fields = Object.keys(this.currentForm)
            let isValid = true
            fields.forEach((field) => {
                if (!this.validateField(field, type)) isValid = false
            })
            if (!isValid) return

            // Garde-fou propagation : un simple renommage ne doit pas réinitialiser
            // les salles sœurs. Si le mode ou la grille a changé et que l'espace est
            // lié à des prestations (mode 0 comme mode 1), exiger une confirmation
            // explicite avant d'enregistrer.
            if (type === 'salle' && this.hasId && !this.sharedWarningConfirmed && this.scheduleHasChanged()) {
                await this.fetchLinkedExperiences()
                if (this.linkedCount > 0) {
                    this.showSharedSpaceModal = true
                    return
                }
            }
            await this.proceedSave()
        },
        async proceedSave() {
            const type = this.resourceType
            this.isLoading = true
            const isUpdating = this.hasId
            try {
                const payload = {
                    ...this.currentForm,
                    type_equipement: type,
                }

                if (type === 'salle') {
                    payload.karafun_devices = this.formSalle.karafun ? this.formSalle.devices : []
                    // N'envoyer le choix horaires que s'il a réellement changé (édition) :
                    // un renommage/capacité ne doit pas retrigger la propagation backend.
                    // Les appelants historiques sans has_custom_hours laissent la grille inchangée.
                    if (!isUpdating || this.scheduleHasChanged()) {
                        payload.has_custom_hours = this.formSalle.has_custom_hours == 1
                        if (payload.has_custom_hours) {
                            payload.days = this.toApiDays()
                        }
                    } else {
                        delete payload.has_custom_hours
                    }
                }

                const response = await axios.patch('/api/v3/equipements/update', payload, {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })

                const msg = isUpdating
                    ? this.$t('equipements.notifications.success_update')
                    : this.$t('equipements.notifications.success_add')

                this.$toast.success(msg)
                this.$emit('save-success')
                this.close()
            } catch (e) {
                if (e.response?.status === 422) {
                    this.$set(this.errors, type, e.response.data.errors)
                }
            } finally {
                this.isLoading = false
            }
        },
    },
}
</script>
